import type { HydraCollection } from './types';

import { useApiRead } from 'api-read-hook';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';

import { Api } from './api';
import {
  AppLoading,
  Layout,
  PageError,
  Text,
  TimerPageTitle,
} from './components';
import ClientDataContext from './contexts/ClientDataContext';
import TimerProvider from './contexts/TimerContext';
import { useAuthUser } from './hooks';
import { Model } from './models';
import * as AgencyDashboard from './pods/agency-dashboard';
import * as AgencyProjects from './pods/agency-projects';
import * as ClientBi from './pods/client-bi';
import * as ClientBilling from './pods/client-billing';
import * as ClientCompany from './pods/client-company';
import * as ClientDashboard from './pods/client-dashboard';
import * as ClientReleases from './pods/client-releases';
import * as ClientTickets from './pods/client-tickets';
import * as DesignResources from './pods/design-resources';
import * as Environments from './pods/environments';
import * as Operations from './pods/operations';
import * as Profile from './pods/profile';
import * as Uptime from './pods/uptime';
import * as Wishlist from './pods/wishlist';
import { isAgency } from './reducers/auth';

/** `/tickets/report` now lives at `/tickets/report/monthly` and `/weekly`. */
function ReportRedirect() {
  const { companySlug } = useParams<{ companySlug: string }>();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const mode = params.get('interval') === 'week' ? 'weekly' : 'monthly';
  params.delete('interval');
  const query = params.size ? `?${params.toString()}` : '';
  return (
    <Navigate
      to={`/company/${companySlug}/tickets/report/${mode}${query}`}
      replace
    />
  );
}

export default function AppAuthed() {
  const authUser = useAuthUser();

  const apiMyUser = Api.User.useReadItemSelf(authUser.id);
  const user = apiMyUser.data;
  const apiTicketLabels = Api.TicketLabel.useReadCollectionPlatformWide();
  const ticketLabels = apiTicketLabels.data?.['hydra:member'];
  const apiCompanies = useApiRead<HydraCollection<Api.Company.V_AdminList>>(
    isAgency(authUser) ? '/companies' : null
  );
  const companies = apiCompanies.data?.['hydra:member'];

  if (apiMyUser.error) return <PageError {...apiMyUser} />;
  if (apiCompanies.error) return <PageError {...apiCompanies} />;
  if (apiTicketLabels.error) return <PageError {...apiTicketLabels} />;
  if (!user || !ticketLabels || (isAgency(authUser) && !companies))
    return <AppLoading />;

  const clientCompanies = user.userToCompanies
    .map((utc) => utc.company)
    .sort(Model.Company.compareCompanies);
  const agencyCompanies = companies
    ? companies.sort(Model.Company.compareCompanies)
    : [];
  const defaultCompany = clientCompanies[0] ?? agencyCompanies[0];
  const agencyDashboardPath = Model.User.getDashboardPath(user);
  // Clients start on the maintenance (tickets) list of their first company,
  // falling back to the My Companies page when they belong to none.
  const rootPath =
    agencyDashboardPath ??
    (defaultCompany ? `/company/${defaultCompany.slug}/tickets` : '/dashboard');

  return (
    <ClientDataContext.Provider
      value={{ user, clientCompanies, agencyCompanies, ticketLabels }}
    >
      <TimerProvider enabled={isAgency(authUser)}>
        <TimerPageTitle />
        <Layout>
          <Routes>
            <Route path="/" element={<Navigate to={rootPath} replace />} />

            <Route
              path="/company/:companySlug"
              element={<ClientCompany.CompanyPage />}
            />
            <Route
              path="/company/:companySlug/bi"
              element={<Navigate to="kpi" replace />}
            />
            <Route
              path="/company/:companySlug/bi/kpi"
              element={<ClientBi.KpisPage />}
            />
            <Route
              path="/company/:companySlug/bi/kpi/:kpiSlug"
              element={<ClientBi.KpiPage />}
            />
            <Route
              path="/company/:companySlug/bi/realtime"
              element={<ClientBi.RealtimePage />}
            />
            <Route
              path="/company/:companySlug/bi/data"
              element={<ClientBi.DataSetsPage />}
            />
            <Route
              path="/company/:companySlug/bi/data/:dataSetId"
              element={<ClientBi.DataPage />}
            />
            <Route
              path="/company/:companySlug/billing"
              element={<ClientBilling.BillingPage />}
            />
            <Route
              path="/company/:companySlug/releases"
              element={<ClientReleases.ReleasesPage />}
            />
            <Route
              path="/company/:companySlug/releases/:versionNumber"
              element={<ClientReleases.ReleasePage />}
            />
            <Route
              path="/company/:companySlug/development/environments"
              element={<Environments.EnvironmentsPage />}
            />
            <Route
              path="/company/:companySlug/development/environments/:deploymentId/commits"
              element={<Environments.CommitHistoryPage />}
            />
            <Route
              path="/company/:companySlug/uptime"
              element={<Uptime.UptimePage />}
            />
            <Route
              path="/company/:companySlug/design/resources"
              element={<DesignResources.DesignResourcesPage />}
            />
            <Route
              path="/company/:companySlug/wishlist"
              element={<Wishlist.WishlistPage />}
            />
            <Route
              path="/company/:companySlug/wishlist/:ideaId"
              element={<Wishlist.IdeaDetailPage />}
            />
            <Route
              path="/company/:companySlug/tickets"
              element={<ClientTickets.TicketsPage />}
            />
            <Route
              path="/company/:companySlug/tickets/new"
              element={<ClientTickets.CreateTicketPage />}
            />
            <Route
              path="/company/:companySlug/tickets/report/:reportMode"
              element={<ClientTickets.ReportPage />}
            />
            <Route
              path="/company/:companySlug/tickets/report"
              element={<ReportRedirect />}
            />
            <Route
              path="/company/:companySlug/tickets/:ticketNumber"
              element={<ClientTickets.TicketPage />}
            />

            <Route
              path="/dashboard"
              element={<ClientDashboard.DashboardPage />}
            />

            {agencyDashboardPath && (
              <>
                <Route
                  path="/dashboard/tasks/:userId"
                  element={<AgencyDashboard.TasksDashboardPage />}
                />
                <Route
                  path="/dashboard/tickets"
                  element={<AgencyDashboard.TicketsDashboardPage />}
                />
              </>
            )}

            {isAgency(authUser) && (
              <>
                <Route
                  path="/projects"
                  element={<AgencyProjects.ProjectsPage />}
                />
                <Route
                  path="/projects/activity"
                  element={<AgencyProjects.DailyActivityPage />}
                />
                <Route
                  path="/projects/service-orders/new"
                  element={<AgencyProjects.NewServiceOrderPage />}
                />
                <Route
                  path="/projects/service-orders/:serviceOrderId"
                  element={<AgencyProjects.ServiceOrderPage />}
                />
                <Route
                  path="/profile/time-log"
                  element={<Profile.TimeLogPage />}
                />
              </>
            )}

            <Route path="/operations" element={<Operations.IndexPage />} />
            <Route
              path="/operations/:slug"
              element={<Operations.SectionPage />}
            />

            <Route path="/profile" element={<Profile.ProfilePage />} />

            <Route path="/login" element={<Navigate to="/" replace />} />

            <Route path="*" element={<Text as="div">Not found</Text>} />
          </Routes>
        </Layout>
      </TimerProvider>
    </ClientDataContext.Provider>
  );
}
